import dayjs from "dayjs";
import { PublicHolidayApi } from "../types/publicHolidayApi";
import { isError } from "../utilities/typeguards";
import { baseFetch, HttpVerbs } from "./baseFetch";
import { PublicHoliday } from "../types/publicHoliday";
import { mapHolidaysFromApi } from "../utilities/publicHolidayHelpers";
import { endpoints } from "./endpoints";
import { useQuery } from "@tanstack/react-query";

const getFullYearsBetween = (startDate: Date, endDate: Date) => {
  const startYear = dayjs(startDate).year();
  const endYear = dayjs(endDate).year();
  const years = [];

  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  return years;
};

type PublicHolidayServiceParams = {
  searchPeriod: { from: string; to: string };
  country: string;
};

const fetchPublicHolidays = async (
  params: PublicHolidayServiceParams
): Promise<PublicHoliday[]> => {
  const from = new Date(params.searchPeriod.from);
  const to = new Date(params.searchPeriod.to);
  const yearsToRetrieve = getFullYearsBetween(from, to);

  // Create an array of promises for each year to retrieve
  const fetchPromises = yearsToRetrieve.map((year) => {
    const endpointUrl = `${year}/${params.country}`;
    return baseFetch<PublicHolidayApi[]>({
      baseUrl: endpoints.publicHolidayBase,
      endpointUrl: endpointUrl,
      httpVerb: HttpVerbs.GET
    });
  });

  try {
    // Use Promise.all to fetch data for all years concurrently
    const responses = await Promise.all(fetchPromises);

    // Check for errors in any of the responses
    const allData = [];
    for (const response of responses) {
      if (isError(response)) {
        // If any response has an error, return early with an error status
        throw new Error(
          `Error fetching public holiday data: ${response.message}`
        );
      }
      // Map and merge the data from successful responses
      allData.push(...mapHolidaysFromApi(response));
    }

    return allData;
  } catch (error) {
    // Handle any unexpected errors
    throw new Error(`Error fetching public holiday data: ${error}`);
  }
};

const usePublicHolidayQuery = (params: PublicHolidayServiceParams) => {
  return useQuery({
    queryKey: [
      "publicHolidays",
      params.searchPeriod.from,
      params.searchPeriod.to,
    ],
    queryFn: () =>
      fetchPublicHolidays({
        searchPeriod: params.searchPeriod,
        country: params.country,
      }),
    // Consider data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep unused data in cache for 30 minutes
    gcTime: 30 * 60 * 1000,
  });
};

export { fetchPublicHolidays, usePublicHolidayQuery };
export type { PublicHolidayServiceParams };
